/**
 * WBCButton — the WeBuyCars button, ported from the website frontend's
 * `components-new/form-controls/WBCButton.jsx` minus the MUI dependency.
 * Renders an <a> when `href` is given, otherwise a <button>.
 *
 * Inputs: color, variant, fullWidth, disabled,
 *         startIcon, endIcon, href, children, className
 *
 * e.g. <WBCButton color="primary" variant="contained" fullWidth
 *        href={url}>Sell my car</WBCButton>
 *      <WBCButton color="white" variant="outlined" onClick={fn}>Cancel</WBCButton>
 *      <WBCButton color="text" variant="light">Skip</WBCButton>
 */

/**
 * @typedef {Object} WBCButtonProps
 * @property {'primary' | 'secondary' | 'tertiary' | 'text' | 'white' | 'red'} [color]
 * @property {'contained' | 'outlined' | 'light' | 'dark'} [variant]  light/dark are for color="text"
 * @property {boolean} [fullWidth]
 * @property {boolean} [disabled]
 * @property {React.ReactNode} [startIcon]
 * @property {React.ReactNode} [endIcon]
 * @property {string} [href]
 * @property {React.ReactNode} [children]
 * @property {string} [className]
 */

/** @param {WBCButtonProps} props */
const WBCButton = ({
  color = "primary",
  variant = "contained",
  fullWidth = false,
  disabled = false,
  startIcon,
  endIcon,
  href,
  children,
  className,
  ...props
}) => {
  const classes = [
    "wbc-btn",
    `wbc-btn--${color}`,
    `wbc-btn--${variant}`,
    fullWidth ? "wbc-btn--full-width" : null,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const content = (
    <>
      {startIcon ? <span className="wbc-btn__start-icon">{startIcon}</span> : null}
      <span className="wbc-btn__label">{children}</span>
      {endIcon ? <span className="wbc-btn__end-icon">{endIcon}</span> : null}
    </>
  );

  if (href !== undefined) {
    return (
      <a
        {...props}
        href={disabled ? undefined : href}
        aria-disabled={disabled || undefined}
        className={classes}
      >
        {content}
      </a>
    );
  }

  return (
    <button type="button" {...props} disabled={disabled} className={classes}>
      {content}
    </button>
  );
};

export default WBCButton;
